import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import ClientDashboard from "@/pages/ClientDashboard";
import WorkerDashboard from "@/pages/WorkerDashboard";
import WorkersPage from "@/pages/Workers";
import TimesheetsList, { TimesheetForm } from "@/pages/Timesheets";
import InvoicesPage from "@/pages/Invoices";
import VitalsPage from "@/pages/Vitals";
import IncidentsPage from "@/pages/Incidents";
import KilometresPage from "@/pages/Kilometres";
import RosterPage from "@/pages/Roster";
import NotificationsPage from "@/pages/Notifications";
import SettingsPage from "@/pages/Settings";
import PlanManagerPage from "@/pages/PlanManager";
import BudgetPage from "@/pages/Budget";
import FinancialInsights from "@/pages/FinancialInsights";
import PayrollTaxPage from "@/pages/PayrollTax";
import FamilyFriendsPage from "@/pages/FamilyFriends";
import ClientProfilePage from "@/pages/ClientProfile";
import WorkerProfilePage from "@/pages/WorkerProfile";
import MyClientsPage from "@/pages/MyClients";
import InboxPage from "@/pages/Inbox";
import SubscriptionPage from "@/pages/Subscription";
import AdminPage from "@/pages/Admin";
import MedicalStaffPage from "@/pages/MedicalStaff";
import TeamChatPage from "@/pages/TeamChat";
import HelpPage from "@/pages/Help";
import api from "@/lib/api";

function HomeRouter() {
  const { user } = useAuth();
  return user?.role === "worker" ? <WorkerDashboard /> : <ClientDashboard />;
}

function ThemeApplier() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        document.documentElement.classList.toggle("dark", data.theme === "dark");
        if (data.theme_preset) {
          const { applyThemePreset } = await import("@/lib/themes");
          applyThemePreset(data.theme_preset);
        }
        if (data.background_url) {
          document.body.style.backgroundImage = `linear-gradient(hsl(var(--background)/0.86), hsl(var(--background)/0.86)), url(${data.background_url})`;
          document.body.style.backgroundSize = "cover";
          document.body.style.backgroundAttachment = "fixed";
        } else {
          document.body.style.backgroundImage = "";
        }
      } catch { /* ignore */ }
    })();
  }, [user]);
  return null;
}

function ShellOutlet() {
  return <Layout><Outlet /></Layout>;
}

function AppRouter() {
  const loc = useLocation();
  // CRITICAL: detect Google OAuth callback BEFORE AuthProvider's /me check runs.
  // The hash check must happen during render (not useEffect) to avoid race conditions.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route element={<ProtectedRoute><ShellOutlet /></ProtectedRoute>}>
        <Route index element={<HomeRouter />} />
        <Route path="workers" element={<WorkersPage />} />
        <Route path="timesheets" element={<TimesheetsList />} />
        <Route path="timesheets/new" element={<TimesheetForm />} />
        <Route path="timesheets/:id" element={<TimesheetForm />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="vitals" element={<VitalsPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="kilometres" element={<KilometresPage />} />
        <Route path="roster" element={<RosterPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="notifications" element={<ProtectedRoute allow={["client"]}><NotificationsPage /></ProtectedRoute>} />
        <Route path="plan-manager" element={<ProtectedRoute allow={["client"]}><PlanManagerPage /></ProtectedRoute>} />
        <Route path="budget" element={<ProtectedRoute allow={["client"]}><BudgetPage /></ProtectedRoute>} />
        <Route path="financial" element={<ProtectedRoute allow={["client"]}><FinancialInsights /></ProtectedRoute>} />
        <Route path="payroll-tax" element={<ProtectedRoute allow={["client"]}><PayrollTaxPage /></ProtectedRoute>} />
        <Route path="family" element={<ProtectedRoute allow={["client"]}><FamilyFriendsPage /></ProtectedRoute>} />
        <Route path="profile" element={<ProtectedRoute allow={["client"]}><ClientProfilePage /></ProtectedRoute>} />
        <Route path="my-profile" element={<ProtectedRoute allow={["worker"]}><WorkerProfilePage /></ProtectedRoute>} />
        <Route path="my-clients" element={<ProtectedRoute allow={["worker"]}><MyClientsPage /></ProtectedRoute>} />
        <Route path="inbox" element={<ProtectedRoute allow={["worker"]}><InboxPage /></ProtectedRoute>} />
        <Route path="subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
        <Route path="admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="medical-staff" element={<ProtectedRoute><MedicalStaffPage /></ProtectedRoute>} />
        <Route path="team-chat" element={<ProtectedRoute><TeamChatPage /></ProtectedRoute>} />
        <Route path="help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeApplier />
        <Toaster richColors position="top-right" />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
